import { useEffect, useMemo, useState } from 'react'
import type { SistemaArchitectureNode } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  defaultSistemaNodeFormData,
  validateSistemaNodeForm,
  type SistemaNodeFormData,
} from './sistemaArchitectureForm.schema'

type SistemaNodeSheetProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (payload: SistemaNodeFormData) => void
  node?: SistemaArchitectureNode | null
  kind: 'BLOCK' | 'ELEMENT'
  isSaving?: boolean
}

function toFormData(node?: SistemaArchitectureNode | null): SistemaNodeFormData {
  if (node == null) {
    return defaultSistemaNodeFormData
  }
  return {
    name: node.name,
  }
}

function resolveTitleKey(
  kind: 'BLOCK' | 'ELEMENT',
  isEdit: boolean
): string {
  if (kind === 'BLOCK') {
    if (isEdit) {
      return 'safetyFunctionsPage.architecture.editBlock'
    }
    return 'safetyFunctionsPage.architecture.addBlock'
  }
  if (isEdit) {
    return 'safetyFunctionsPage.architecture.editElement'
  }
  return 'safetyFunctionsPage.architecture.addElement'
}

export function SistemaNodeSheet({
  isOpen,
  onClose,
  onSave,
  node,
  kind,
  isSaving = false,
}: SistemaNodeSheetProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const isEdit = node != null
  const [formData, setFormData] = useState<SistemaNodeFormData>(() =>
    toFormData(node)
  )

  useEffect(() => {
    if (isOpen) {
      setFormData(toFormData(node))
    }
  }, [isOpen, node])

  const isValid = useMemo(() => {
    return validateSistemaNodeForm(formData).success
  }, [formData])

  const titleKey = resolveTitleKey(kind, isEdit)

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col overflow-hidden sm:max-w-md'
      >
        <SheetHeader className='shrink-0'>
          <SheetTitle>{t(titleKey)}</SheetTitle>
        </SheetHeader>

        <ScrollArea className='min-h-0 flex-1'>
          <div className='space-y-2 px-4 pb-4'>
            <Label htmlFor='node-name'>
              {t('safetyFunctionsPage.architecture.name')}{' '}
              <span className='text-destructive'>*</span>
            </Label>
            <Input
              id='node-name'
              value={formData.name}
              onChange={(event) =>
                setFormData({ ...formData, name: event.target.value })
              }
            />
          </div>
        </ScrollArea>

        <SheetFooter className='shrink-0'>
          <Button
            onClick={() => {
              if (isValid) {
                onSave(formData)
              }
            }}
            disabled={!isValid || isSaving}
          >
            {isSaving ? <Loader2 className='mr-2 size-4 animate-spin' /> : null}
            {t('common:actions.save')}
          </Button>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
